/**
 * The stint chart (plan item 5b): one row per car, each stint a bar on the race clock, coloured
 * by its green-flag pace against the class's best or by its driver; a tick where each stop was
 * (yellow under a caution, orange into the garage), ▲ for a drive-through, and the race's
 * cautions shaded across every row. The model is stint-gantt.ts.
 *
 * Laptop: hover a bar for its stint; click a car to open its stints and stops; drag across the
 * chart to zoom to a stretch, double-click for the whole race. Phone: tap a bar for its stint;
 * pinch to zoom the time, drag sideways when zoomed, double-tap to zoom in or out. The page still
 * scrolls up and down over the chart.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AnnotationData, RaceChartData } from "@shared/types";
import { CHART_STYLE, CLASS_COLORS, TRACE } from "./constants";
import {
  DRIVER_COLORS,
  NO_DRIVER,
  barAt,
  barLines,
  barRgb,
  clampWindow,
  clockText,
  doubleTapWindow,
  ganttModel,
  readColorBy,
  writeColorBy,
  isZoomed,
  panWindow,
  pinchWindow,
  timeTicks,
  type GanttBar,
  type GanttColorBy,
  type GanttModel,
  type GanttRow,
  type TimeWindow,
} from "./stint-gantt";
import {
  STINT_FAST,
  STINT_MID,
  STINT_SLOW,
  STINT_TEXT,
  STRATEGY_KEY,
  rgbCss,
} from "./strategy-view";

/** The tick at a stop: pink, yellow under a caution, orange into the garage. */
export const GANTT_STOP = "#ff6b9d";
export const GANTT_STOP_CAUTION = TRACE.fcyBar;
export const GANTT_STOP_GARAGE = TRACE.garage;
const FCY_FILL = "rgba(251,191,36,0.10)";
const FOCUS_ROW = "rgba(244,140,52,0.12)";
const PICKED_ROW = "rgba(255,255,255,0.06)";

interface Layout {
  W: number;
  H: number;
  /** Left gutter for the car labels. */
  LW: number;
  /** Plot width. */
  PW: number;
  rowH: number;
  /** Space under the rows for the time axis. */
  AX: number;
  top: number;
}

function layoutFor(width: number, rows: number, compact: boolean): Layout {
  const LW = compact ? 52 : 170;
  const rowH = compact ? 28 : 22;
  const AX = 22;
  const top = 4;
  return {
    W: width,
    H: top + rows * rowH + AX,
    LW,
    PW: Math.max(40, width - LW - 8),
    rowH,
    AX,
    top,
  };
}

const DOUBLE_MS = 300;
const SLOP = 8;

export function StintGantt({
  model,
  colorBy,
  compact = false,
  selectedKey = null,
  onPickCar,
  onWindow,
  resetSignal = 0,
}: {
  model: GanttModel;
  colorBy: GanttColorBy;
  /** A phone: narrow labels, taller rows, touch gestures. */
  compact?: boolean;
  selectedKey?: string | null;
  /** A car's row clicked (laptop) or its label tapped (phone). */
  onPickCar?: (num: number) => void;
  /** The window shown changed (the page shows a Whole race button when zoomed). */
  onWindow?: (zoomed: boolean) => void;
  /** Bumped by the page's Whole race button. */
  resetSignal?: number;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);
  const endS = Math.max(1, model.endS);
  const [win, setWin] = useState<TimeWindow>([0, endS]);
  const [hover, setHover] = useState<{
    row: GanttRow;
    bar: GanttBar;
    x: number;
    y: number;
  } | null>(null);
  const [picked, setPicked] = useState<{ row: GanttRow; bar: GanttBar } | null>(null);
  const [drag, setDrag] = useState<[number, number] | null>(null);

  const L = useMemo(
    () => layoutFor(width, model.rows.length, compact),
    [width, model.rows.length, compact]
  );

  // A new race (or a live update past the window): the whole race again.
  useEffect(() => setWin([0, endS]), [endS, resetSignal]);
  useEffect(() => onWindow?.(isZoomed(win, endS)), [win, endS, onWindow]);
  useEffect(() => setPicked(null), [model]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : undefined;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const xOf = useCallback(
    (s: number) => L.LW + ((s - win[0]) / Math.max(1, win[1] - win[0])) * L.PW,
    [L, win]
  );
  const timeAt = useCallback(
    (x: number) => win[0] + ((x - L.LW) / Math.max(1, L.PW)) * (win[1] - win[0]),
    [L, win]
  );
  const rowAt = (y: number) => {
    const i = Math.floor((y - L.top) / L.rowH);
    return i >= 0 && i < model.rows.length ? model.rows[i] : null;
  };

  // ── Draw ──
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !width) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = L.W * dpr;
    cv.height = L.H * dpr;
    cv.style.width = `${L.W}px`;
    cv.style.height = `${L.H}px`;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, L.W, L.H);
    const plotTop = L.top;
    const plotBot = L.top + model.rows.length * L.rowH;
    const x0 = L.LW;
    const x1 = L.LW + L.PW;

    // Row backgrounds: the focus car and the car picked.
    model.rows.forEach((r, i) => {
      const y = plotTop + i * L.rowH;
      if (r.focus || r.key === selectedKey) {
        ctx.fillStyle = r.focus ? FOCUS_ROW : PICKED_ROW;
        ctx.fillRect(0, y, L.W, L.rowH);
      }
    });

    ctx.save();
    ctx.beginPath();
    ctx.rect(x0, 0, L.PW, L.H);
    ctx.clip();
    // Cautions, every row.
    for (const c of model.cautions) {
      if (c.endS < win[0] || c.startS > win[1]) continue;
      const a = xOf(c.startS);
      const b = xOf(c.endS);
      ctx.fillStyle = FCY_FILL;
      ctx.fillRect(a, plotTop, Math.max(1, b - a), plotBot - plotTop);
      ctx.fillStyle = TRACE.fcyBar;
      ctx.fillRect(a, plotBot, Math.max(1, b - a), 2);
    }
    // Hour lines.
    const ticks = timeTicks(win, L.PW, compact ? 52 : 64);
    ctx.strokeStyle = CHART_STYLE.gridLine;
    ctx.lineWidth = 1;
    for (const t of ticks) {
      const x = Math.round(xOf(t)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(x, plotTop);
      ctx.lineTo(x, plotBot);
      ctx.stroke();
    }
    // Bars, stop ticks, drive-throughs.
    const barPad = compact ? 4 : 3;
    ctx.font = `600 ${compact ? 11 : 10}px Inter, system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    model.rows.forEach((r, i) => {
      const y = plotTop + i * L.rowH + barPad;
      const h = L.rowH - 2 * barPad;
      for (const b of r.bars) {
        const s = b.stint;
        if (s.endS < win[0] || s.startS > win[1]) continue;
        const a = xOf(s.startS);
        const e = xOf(s.endS);
        // One pixel apart from the stint before, so back-to-back stints read as two.
        const left = s.n > 1 ? a + 1 : a;
        const w = Math.max(1, e - left);
        ctx.fillStyle = rgbCss(barRgb(b, r, colorBy));
        ctx.fillRect(left, y, w, h);
        const label =
          colorBy === "driver" && s.driver
            ? (s.driver.split(/\s+/).pop() ?? "")
            : String(s.endLap - s.startLap + 1);
        if (label && ctx.measureText(label).width + 6 <= w) {
          ctx.fillStyle = STINT_TEXT;
          ctx.fillText(label, left + w / 2, y + h / 2 + 0.5);
        }
        if (s.end === "pit_in") {
          ctx.fillStyle =
            s.stop?.kind === "garage"
              ? GANTT_STOP_GARAGE
              : s.stop?.caution
                ? GANTT_STOP_CAUTION
                : GANTT_STOP;
          ctx.fillRect(Math.round(e) - 1, y - 2, 2, h + 4);
        }
      }
      for (const d of r.driveThroughs) {
        if (d.atS < win[0] || d.atS > win[1]) continue;
        const x = xOf(d.atS);
        ctx.fillStyle = TRACE.glyph;
        ctx.beginPath();
        ctx.moveTo(x, y + 1);
        ctx.lineTo(x - 4, y + 7);
        ctx.lineTo(x + 4, y + 7);
        ctx.closePath();
        ctx.fill();
      }
    });
    // A drag to zoom (laptop).
    if (drag) {
      const a = Math.min(drag[0], drag[1]);
      ctx.fillStyle = "rgba(255,255,255,0.10)";
      ctx.fillRect(a, plotTop, Math.abs(drag[1] - drag[0]), plotBot - plotTop);
    }
    ctx.restore();

    // Car labels.
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    model.rows.forEach((r, i) => {
      const y = plotTop + i * L.rowH + L.rowH / 2;
      ctx.font = `700 ${compact ? 12 : 12}px "JetBrains Mono", ui-monospace, monospace`;
      ctx.fillStyle = r.focus ? TRACE.focus : (CLASS_COLORS[r.cls] ?? CHART_STYLE.text);
      ctx.fillText(r.label, 8, y);
      if (!compact) {
        const lw = ctx.measureText(r.label).width;
        ctx.font = `400 12px Inter, system-ui, sans-serif`;
        ctx.fillStyle = CHART_STYLE.text;
        let team = r.team.replace(/\s*\([^()]*\)\s*$/, "");
        const room = L.LW - 8 - lw - 14;
        while (team.length > 1 && ctx.measureText(team).width > room)
          team = team.slice(0, -2) + "…";
        ctx.fillText(team, 8 + lw + 6, y);
      }
    });

    // Time axis.
    ctx.font = `400 11px Inter, system-ui, sans-serif`;
    ctx.fillStyle = TRACE.axis;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (const t of ticks) {
      const x = xOf(t);
      if (x > x1 - 4) continue;
      ctx.fillText(clockText(t), x, plotBot + 6);
    }
    // Where the window starts, unless a tick label is there already.
    if (!ticks.some((t) => xOf(t) < x0 + 44)) {
      ctx.textAlign = "left";
      ctx.fillText(clockText(win[0]), x0, plotBot + 6);
    }
  }, [model, colorBy, width, L, win, xOf, compact, selectedKey, drag]);

  // ── Laptop: hover, click, drag to zoom, double-click for the whole race ──
  const local = (e: { clientX: number; clientY: number }) => {
    const r = wrapRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const dragFrom = useRef<number | null>(null);
  const onMouseMove = (e: React.MouseEvent) => {
    const p = local(e);
    if (dragFrom.current !== null) {
      setDrag([dragFrom.current, Math.min(Math.max(p.x, L.LW), L.LW + L.PW)]);
      setHover(null);
      return;
    }
    const row = rowAt(p.y);
    const bar = row && p.x >= L.LW ? barAt(row, timeAt(p.x)) : null;
    setHover(row && bar ? { row, bar, x: p.x, y: p.y } : null);
  };
  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const p = local(e);
    if (p.x >= L.LW) dragFrom.current = p.x;
  };
  const onMouseUp = (e: React.MouseEvent) => {
    const p = local(e);
    const from = dragFrom.current;
    dragFrom.current = null;
    setDrag(null);
    if (from !== null && Math.abs(p.x - from) > SLOP) {
      const a = timeAt(Math.min(from, p.x));
      const b = timeAt(Math.max(from, p.x));
      setWin(clampWindow(a, b - a, endS));
      return;
    }
    const row = rowAt(p.y);
    if (row) onPickCar?.(row.num);
  };

  // ── Phone: tap, pinch, drag when zoomed, double-tap ──
  const onPickRef = useRef(onPickCar);
  onPickRef.current = onPickCar;
  const winRef = useRef(win);
  winRef.current = win;
  const geo = useRef({ L, endS, rows: model.rows });
  geo.current = { L, endS, rows: model.rows };
  useEffect(() => {
    const el = canvasRef.current;
    if (!el || !compact) return;
    // The page keeps its own up-and-down scrolling; sideways and pinches are the chart's.
    el.style.touchAction = "pan-y";
    type Pt = { x: number; y: number };
    let mode: "none" | "tap" | "pan" | "pinch" = "none";
    let start: Pt = { x: 0, y: 0 };
    let startWin: TimeWindow = [0, 1];
    let dist0 = 1;
    let at0 = 0;
    let lastTap: { t: number; x: number; y: number } | null = null;
    const pos = (t: Touch): Pt => {
      const r = el.getBoundingClientRect();
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const tAt = (x: number, w: TimeWindow) => {
      const { L: l } = geo.current;
      return w[0] + ((x - l.LW) / Math.max(1, l.PW)) * (w[1] - w[0]);
    };
    const onStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        e.preventDefault();
        const a = pos(e.touches[0]);
        const b = pos(e.touches[1]);
        mode = "pinch";
        startWin = winRef.current;
        dist0 = Math.hypot(a.x - b.x, a.y - b.y);
        at0 = tAt((a.x + b.x) / 2, startWin);
        return;
      }
      mode = "tap";
      start = pos(e.touches[0]);
      startWin = winRef.current;
    };
    const onMove = (e: TouchEvent) => {
      const { L: l, endS: end } = geo.current;
      if (mode === "pinch" && e.touches.length >= 2) {
        e.preventDefault();
        const a = pos(e.touches[0]);
        const b = pos(e.touches[1]);
        const mid = (a.x + b.x) / 2;
        setWin(
          pinchWindow(
            startWin,
            at0,
            dist0,
            Math.hypot(a.x - b.x, a.y - b.y),
            (mid - l.LW) / Math.max(1, l.PW),
            end
          )
        );
        return;
      }
      if (mode === "none" || !e.touches.length) return;
      const p = pos(e.touches[0]);
      const dx = p.x - start.x;
      const dy = p.y - start.y;
      if (mode === "tap") {
        if (Math.hypot(dx, dy) < SLOP) return;
        // Sideways and zoomed in: move along the race. Otherwise the page scrolls.
        mode = Math.abs(dx) > Math.abs(dy) && isZoomed(startWin, end) ? "pan" : "none";
      }
      if (mode === "pan") {
        if (e.cancelable) e.preventDefault();
        setWin(panWindow(startWin, dx, l.PW, end));
      }
    };
    const onEnd = (e: TouchEvent) => {
      if (e.touches.length > 0) return;
      const was = mode;
      mode = "none";
      if (was !== "tap") return;
      const ct = e.changedTouches[0];
      const p = pos(ct);
      const { L: l, endS: end, rows } = geo.current;
      const now = performance.now();
      if (
        lastTap &&
        now - lastTap.t <= DOUBLE_MS &&
        Math.hypot(p.x - lastTap.x, p.y - lastTap.y) <= 30 &&
        p.x >= l.LW
      ) {
        lastTap = null;
        if (e.cancelable) e.preventDefault();
        setWin(doubleTapWindow(winRef.current, tAt(p.x, winRef.current), end));
        return;
      }
      lastTap = { t: now, x: p.x, y: p.y };
      const i = Math.floor((p.y - l.top) / l.rowH);
      const row = i >= 0 && i < rows.length ? rows[i] : null;
      if (!row) return setPicked(null);
      if (p.x < l.LW) return void onPickRef.current?.(row.num);
      const bar = barAt(row, tAt(p.x, winRef.current));
      setPicked(bar ? { row, bar } : null);
    };
    el.addEventListener("touchstart", onStart, { passive: false });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
    };
  }, [compact]);
  const tip = hover ? barLines(hover.row, hover.bar) : null;
  const shown = picked ? barLines(picked.row, picked.bar) : null;
  const summary = `Stints on the race clock for ${model.rows.length} car${model.rows.length === 1 ? "" : "s"}, ${clockText(win[0])} to ${clockText(win[1])}`;

  return (
    <div>
      <div ref={wrapRef} className="relative w-full select-none" style={{ height: L.H }}>
        <canvas
          ref={canvasRef}
          // Part of the page, not the screen's chart (the phone check's chart-near-top rule).
          data-in-page-chart
          role="img"
          aria-label={summary}
          className={compact ? "block" : "block cursor-crosshair"}
          onMouseMove={compact ? undefined : onMouseMove}
          onMouseLeave={
            compact
              ? undefined
              : () => {
                  setHover(null);
                  dragFrom.current = null;
                  setDrag(null);
                }
          }
          onMouseDown={compact ? undefined : onMouseDown}
          onMouseUp={compact ? undefined : onMouseUp}
          onDoubleClick={compact ? undefined : () => setWin([0, endS])}
        />
        {tip && hover && (
          <div
            className="pointer-events-none absolute z-10 rounded-md border px-2.5 py-1.5 text-[12px] leading-snug shadow-lg"
            style={{
              left: Math.min(hover.x + 14, Math.max(0, width - 300)),
              top: hover.y + 16,
              background: CHART_STYLE.card,
              borderColor: "#3a3a5c",
              color: CHART_STYLE.text,
              maxWidth: 300,
            }}
          >
            {tip.map((l, i) => (
              <div
                key={i}
                className={i === 0 ? "font-semibold" : undefined}
                style={i > 1 ? { color: "#cbd5e1" } : undefined}
              >
                {l}
              </div>
            ))}
          </div>
        )}
      </div>
      {compact && (
        <div
          className="mt-2 min-h-[40px] text-[13px] leading-snug"
          aria-live="polite"
          style={{ color: CHART_STYLE.text }}
        >
          {shown ? (
            shown.map((l, i) => (
              <div
                key={i}
                className={i === 0 ? "font-semibold" : undefined}
                style={i > 0 ? { color: "#cbd5e1" } : undefined}
              >
                {l}
              </div>
            ))
          ) : (
            <span style={{ color: "#9a9ab4" }}>
              Tap a stint for its laps, times and stop. Pinch to zoom the time.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── The panel: title, colour switch, the chart, its key and how it is worked out ───

const P = {
  line: "#1e1e38",
  border: "#3a3a5c",
  card: "#12122a",
  text: "#e0e0e8",
  muted: "#9a9ab4",
  accent: "#F48C34",
} as const;

export function StintGanttPanel({
  data,
  annotations,
  cars,
  focusKey,
  selectedKey = null,
  onPickCar,
  compact = false,
  title = "Stints on the race clock",
  live = false,
}: {
  data: RaceChartData;
  annotations: AnnotationData | null;
  /** Chart keys, in the order to draw them (the focus car goes first). */
  cars: string[];
  focusKey: string | null;
  selectedKey?: string | null;
  onPickCar?: (num: number) => void;
  compact?: boolean;
  title?: string;
  live?: boolean;
}) {
  const model = useMemo(
    () => ganttModel(data, annotations, { cars, focusKey, live }),
    [data, annotations, cars, focusKey, live]
  );
  const [by, setBy] = useState<GanttColorBy>(readColorBy);
  const colorBy: GanttColorBy = by === "driver" && model.hasDrivers ? "driver" : "pace";
  const [zoomed, setZoomed] = useState(false);
  const [reset, setReset] = useState(0);
  const pick = (v: GanttColorBy) => {
    setBy(v);
    writeColorBy(v);
  };
  if (!model.rows.length) return null;
  const focusRow = model.rows.find((r) => r.focus) ?? null;
  const btn =
    "min-h-[44px] rounded-md border px-3 text-[13px] font-semibold sm:min-h-[30px] sm:text-xs disabled:opacity-40";
  return (
    <section
      aria-label={title}
      className={compact ? "" : "rounded-lg border p-3"}
      style={compact ? undefined : { borderColor: P.line, background: P.card }}
    >
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className="m-0 text-sm font-semibold" style={{ color: P.text }}>
          {title}
        </h3>
        <div className="flex gap-1" role="group" aria-label="Colour the stints by">
          {(["pace", "driver"] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={colorBy === v}
              disabled={v === "driver" && !model.hasDrivers}
              onClick={() => pick(v)}
              title={
                v === "pace"
                  ? "Colour each stint by its green-flag pace against the class's best"
                  : model.hasDrivers
                    ? "Colour each stint by who drove it: each car's first, second, third driver"
                    : "The timing for this race doesn't name who drove each stint"
              }
              className={btn}
              style={
                colorBy === v
                  ? { background: P.accent, borderColor: P.accent, color: "#0b0b18" }
                  : { borderColor: P.border, color: P.muted }
              }
            >
              {v === "pace" ? "Pace" : "Driver"}
            </button>
          ))}
        </div>
        {zoomed && (
          <button
            type="button"
            onClick={() => setReset((n) => n + 1)}
            title="Show the whole race again"
            className={btn}
            style={{ borderColor: P.border, color: P.text }}
          >
            Whole race
          </button>
        )}
      </div>
      <StintGantt
        model={model}
        colorBy={colorBy}
        compact={compact}
        selectedKey={selectedKey}
        onPickCar={onPickCar}
        onWindow={setZoomed}
        resetSignal={reset}
      />
      <GanttKey colorBy={colorBy} focusRow={focusRow} compact={compact} />
      <p
        className="m-0 mt-1.5 max-w-4xl text-xs leading-relaxed"
        style={{ color: P.muted }}
      >
        Each bar is a stint, from the start or a stop to the next stop, on the race clock
        (hours and minutes from the start).{" "}
        {model.source === "package"
          ? "Stints run from leaving the pit lane to entering it, from the race's analysis."
          : "Without pit-lane times, a stint ends where its in-lap crosses the line and the next one starts there."}{" "}
        {compact
          ? "Pinch to zoom the time; double-tap to zoom in or out."
          : "Hover a stint for its laps and stop; drag across the chart to zoom, double-click for the whole race."}
      </p>
    </section>
  );
}

function Swatch({ color, wide = true }: { color: string; wide?: boolean }) {
  return (
    <span
      className={`inline-block shrink-0 rounded-[2px] ${wide ? "h-2.5 w-3" : "h-3 w-[3px]"}`}
      style={{ background: color }}
    />
  );
}

function GanttKey({
  colorBy,
  focusRow,
  compact,
}: {
  colorBy: GanttColorBy;
  focusRow: GanttRow | null;
  compact: boolean;
}) {
  const item = "flex items-center gap-1.5";
  return (
    <div
      className={`mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 ${compact ? "text-[12px]" : "text-xs"}`}
      style={{ color: P.muted }}
    >
      {colorBy === "pace" ? (
        <>
          <span className={item}>
            <Swatch color={rgbCss(STINT_FAST)} /> {STRATEGY_KEY.best}
          </span>
          <span className={item}>
            <Swatch color={rgbCss(STINT_MID)} /> {STRATEGY_KEY.mid}
          </span>
          <span className={item}>
            <Swatch color={rgbCss(STINT_SLOW)} /> {STRATEGY_KEY.slow}
          </span>
        </>
      ) : focusRow?.drivers.length ? (
        <>
          {focusRow.drivers.map((d, i) => (
            <span key={d} className={item}>
              <Swatch
                color={rgbCss(DRIVER_COLORS[Math.min(i, DRIVER_COLORS.length - 1)])}
              />{" "}
              {d}
            </span>
          ))}
          <span>Other cars: their drivers in the same order of colours</span>
        </>
      ) : (
        <>
          {["First", "Second", "Third", "Fourth"].map((d, i) => (
            <span key={d} className={item}>
              <Swatch color={rgbCss(DRIVER_COLORS[i])} /> {d} driver
            </span>
          ))}
          <span className={item}>
            <Swatch color={rgbCss(NO_DRIVER)} /> Not named
          </span>
        </>
      )}
      <span className={item}>
        <Swatch color={GANTT_STOP} wide={false} /> Stop
      </span>
      <span className={item}>
        <Swatch color={GANTT_STOP_CAUTION} wide={false} /> Stop under caution
      </span>
      <span className={item}>
        <Swatch color={GANTT_STOP_GARAGE} wide={false} /> Garage
      </span>
      <span className={item}>
        <span aria-hidden style={{ color: TRACE.glyph }}>
          ▲
        </span>{" "}
        Drive-through
      </span>
      <span className={item}>
        <span
          className="inline-block h-2.5 w-3"
          style={{ background: FCY_FILL, borderBottom: `2px solid ${TRACE.fcyBar}` }}
        />{" "}
        Caution
      </span>
    </div>
  );
}
